<template>
  <div>
    <v-card>
      <v-card-title>
        <span class="headline">Rate Analysis & History</span>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          @click="refreshAnalysis"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Category Selection -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedCategory"
              :items="costCategoryOptions"
              label="Select Cost Category for Analysis"
              no-data-text="Not available"
              outlined
              dense
              @change="analyzeCategory"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-date-picker
              v-model="analysisDateRange"
              range
              no-title
              class="mt-0"
            ></v-date-picker>
          </v-col>
        </v-row>

        <!-- Analysis Results -->
        <div v-if="selectedCategory && analysis">
          <!-- Summary Statistics -->
          <v-row class="mb-4">
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4 primary--text">{{ analysis.totalRates }}</div>
                  <div class="text-caption">Total Rates</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4 success--text">{{ analysis.activeRates }}</div>
                  <div class="text-caption">Active Rates</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4 warning--text">{{ analysis.overlaps.length }}</div>
                  <div class="text-caption">Overlapping Periods</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4 info--text">{{ analysis.rateChanges.length }}</div>
                  <div class="text-caption">Rate Changes</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Issues Section -->
          <div v-if="analysis.overlaps.length > 0" class="mb-4">
            <v-alert type="warning" outlined>
              <v-row align="center">
                <v-col class="grow">
                  <div class="font-weight-medium">Overlapping Rate Periods Detected</div>
                  <div class="text-body-2">
                    {{ analysis.overlaps.length }} overlapping period(s)
                    found that may cause billing inconsistencies.
                  </div>
                </v-col>
                <v-col class="shrink">
                  <v-btn
                    color="warning"
                    outlined
                    small
                    @click="showOverlapsDialog = true"
                  >
                    View Details
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </div>

          <!-- Rate Changes Timeline -->
          <v-card outlined class="mb-4">
            <v-card-title class="pb-2">
              <v-icon left>mdi-chart-line</v-icon>
              Rate Changes Timeline
            </v-card-title>
            <v-card-text>
              <div v-if="analysis.rateChanges.length > 0">
                <v-timeline align-top dense>
                  <v-timeline-item
                    v-for="(change, index) in analysis.rateChanges"
                    :key="index"
                    :color="getChangeColor(change.percentChange)"
                    :icon="getChangeIcon(change.percentChange)"
                    fill-dot
                  >
                    <template v-slot:opposite>
                      <span class="text-caption">
                        {{ formatDate(change.changeDate) }}
                      </span>
                    </template>

                    <v-card class="elevation-1">
                      <v-card-text class="pb-2">
                        <div class="d-flex align-center">
                          <div class="flex-grow-1">
                            <div class="text-body-1 font-weight-medium">
                              {{ formatRate(change.fromRate.rate, change.fromRate.rateType) }}
                              →
                              {{ formatRate(change.toRate.rate, change.toRate.rateType) }}
                            </div>
                            <div class="text-caption grey--text">
                              Change:
                              <span :class="getChangeTextClass(change.percentChange)">
                                {{ formatChange(change.change, change.percentChange) }}
                              </span>
                            </div>
                          </div>
                          <div class="text-right">
                            <v-chip
                              :color="getChangeColor(change.percentChange)"
                              text-color="white"
                              x-small
                            >
                              {{ change.percentChange > 0 ? '+' : '' }}{{
                                change.percentChange.toFixed(1)
                              }}%
                            </v-chip>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </div>
              <div v-else class="text-center py-4">
                <v-icon size="48" color="grey lighten-2">mdi-chart-line</v-icon>
                <div class="text-body-2 grey--text mt-2">No rate changes found</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Coverage Gaps -->
          <v-card outlined v-if="coverageGaps.length > 0">
            <v-card-title class="pb-2">
              <v-icon left color="error">mdi-alert</v-icon>
              Coverage Gaps
            </v-card-title>
            <v-card-text>
              <v-alert type="error" outlined>
                <div class="font-weight-medium mb-2">Rate Coverage Gaps Detected</div>
                <div class="text-body-2 mb-3">
                  The following periods have no rate coverage for
                  {{ formatCostCategory(selectedCategory) }}:
                </div>
                <v-list dense>
                  <v-list-item
                    v-for="(gap, index) in coverageGaps"
                    :key="index"
                  >
                    <v-list-item-icon>
                      <v-icon color="error">mdi-calendar-remove</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ formatDate(gap.startDate) }} to {{ formatDate(gap.endDate) }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-alert>
            </v-card-text>
          </v-card>
        </div>

        <!-- Empty State -->
        <div v-else-if="selectedCategory && !analysis" class="text-center py-8">
          <v-icon size="64" color="grey lighten-2">mdi-chart-box-outline</v-icon>
          <div class="text-h6 grey--text mt-2">No Analysis Data</div>
          <div class="text-body-2 grey--text">
            No rates found for {{ formatCostCategory(selectedCategory) }}
          </div>
        </div>

        <!-- Initial State -->
        <div v-else class="text-center py-8">
          <v-icon size="64" color="grey lighten-2">mdi-analytics</v-icon>
          <div class="text-h6 grey--text mt-2">Select a Cost Category</div>
          <div class="text-body-2 grey--text">
            Choose a cost category above to view detailed rate analysis
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Overlaps Detail Dialog -->
    <v-dialog v-model="showOverlapsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="headline">Overlapping Rate Periods</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showOverlapsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item
              v-for="(overlap, index) in analysis.overlaps"
              :key="index"
            >
              <v-list-item-icon>
                <v-icon color="warning">mdi-alert</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  Rate {{ overlap.rate1.id }} overlaps with Rate {{ overlap.rate2.id }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <div>
                    Rate 1: {{ formatRate(overlap.rate1.rate, overlap.rate1.rateType) }}
                    ({{ formatDate(overlap.rate1.effectiveFrom) }} -
                    {{ formatDate(overlap.rate1.effectiveTo) }})
                  </div>
                  <div>
                    Rate 2: {{ formatRate(overlap.rate2.rate, overlap.rate2.rateType) }}
                    ({{ formatDate(overlap.rate2.effectiveFrom) }} -
                    {{ formatDate(overlap.rate2.effectiveTo) }})
                  </div>
                  <div class="error--text">
                    Overlap Period: {{ formatDate(overlap.overlapStart) }} -
                    {{ formatDate(overlap.overlapEnd) }}
                  </div>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  color="primary"
                  text
                  small
                  @click="resolveOverlap(overlap)"
                >
                  Resolve
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import costCategoryMixin from '../../mixins/costCategoryMixin';

export default {
  name: 'RateAnalysisView',
  mixins: [costCategoryMixin],
  data() {
    return {
      loading: false,
      selectedCategory: null,
      analysis: null,
      coverageGaps: [],
      analysisDateRange: [],
      showOverlapsDialog: false,
    };
  },
  computed: {
    ...mapGetters('rateMaster', ['allRates']),
  },
  created() {
    // Set default analysis date range to current year
    const currentYear = new Date().getFullYear();
    this.analysisDateRange = [
      `${currentYear}-01-01`,
      `${currentYear}-12-31`,
    ];
  },
  methods: {
    async refreshAnalysis() {
      this.loading = true;
      try {
        await this.$store.dispatch('rateMaster/fetchRates');
        if (this.selectedCategory) {
          await this.analyzeCategory();
        }
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error refreshing analysis: ${error.message}`,
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    async analyzeCategory() {
      if (!this.selectedCategory) return;

      try {
        const { RateValidationService } = await import('../../services/rateValidation');

        // Perform rate history analysis
        this.analysis = RateValidationService.analyzeRateHistory(
          this.selectedCategory,
          this.allRates,
        );

        // Find coverage gaps if date range is selected
        if (this.analysisDateRange.length === 2) {
          this.coverageGaps = RateValidationService.findRateGaps(
            this.selectedCategory,
            this.allRates,
            new Date(this.analysisDateRange[0]),
            new Date(this.analysisDateRange[1]),
          );
        } else {
          this.coverageGaps = [];
        }
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error analyzing rates: ${error.message}`,
          color: 'error',
        });
      }
    },

    resolveOverlap(overlap) {
      // Navigate to edit the first rate to resolve the overlap
      this.$router.push(`/master-data/rates/${overlap.rate1.id}/edit`);
    },

    formatRate(rate, rateType) {
      if (rateType === 'PERCENTAGE') {
        return `${rate}%`;
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.$currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(rate);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatChange(change, percentChange) {
      const sign = change >= 0 ? '+' : '';
      return `${sign}${change.toFixed(2)} (${sign}${percentChange.toFixed(1)}%)`;
    },

    getChangeColor(percentChange) {
      if (percentChange > 0) return 'success';
      if (percentChange < 0) return 'error';
      return 'grey';
    },

    getChangeIcon(percentChange) {
      if (percentChange > 0) return 'mdi-trending-up';
      if (percentChange < 0) return 'mdi-trending-down';
      return 'mdi-trending-neutral';
    },

    getChangeTextClass(percentChange) {
      if (percentChange > 0) return 'success--text';
      if (percentChange < 0) return 'error--text';
      return 'grey--text';
    },
  },
};
</script>

<style scoped>
.v-timeline-item__body {
  padding-bottom: 16px;
}
</style>
